import { PropTypes } from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import AccionaTableTabs from '../../../../../components/AccionaTableTabs';
import AccionaLabel from '../../../../../components/AccionaLabel';
import RemoveModal from '../../../../../components/RemoveModal';
import { MainContext } from '../../../../../context/MainContext';
import {
  getCSV,
  getCSVByProject,
  getUnusedNC,
} from '../../../../../services/api/ncs';
import * as NCActions from '../../../../../services/redux/NCs/actions';
import * as PRMActions from '../../../../../services/redux/PRMs/actions';
import {
  NC_BUTTONS,
  NC_COLUMNS_HEADERS,
  PRMS_BUTTONS,
  PRMS_COLUMNS_HEADERS,
} from './columns';
import styles from './index.module.scss';

const Tabs = {
  PRM: 'PRMs',
  NC: 'NCs',
};

const PRMsList = ({
  getPRMsList,
  getNCsList,
  deletePRM,
  deleteNC,
  errorMessages,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { project } = useOutletContext();
  const { setError } = useContext(MainContext);
  const { pathname } = useLocation();
  const { search } = useLocation();
  const { me } = useSelector(({ auth }) => auth);

  const ncId = new URLSearchParams(search).get('nc');
  const pid = project.id_project;

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedTab, setSelectedTab] = useState(Tabs.PRM);
  const [initialFilters, setInitialFilters] = useState(undefined);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [generateCSVButtonState, setGenerateCSVButtonState] = useState(false);
  const { list } = useSelector((state) => state.ncs);
  const [tableState, setTableState] = useState({
    pagination: {
      current: 1,
      pagaSize: 10,
    },
    filters: '',
  });

  const onSingleDelete = (data) => {
    setSelectedRows([data]);
    setShowRemoveModal(true);
  };

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    if (selectedTab === Tabs.PRM) {
      deletePRM(selectedRows.map(({ id }) => id));
    } else {
      deleteNC(selectedRows.map(({ id }) => id));
    }
    setSelectedRows([]);
  };

  const parseFunction = (nc) => {
    return nc.map((e) => ({
      ...e,
      ncNumber: e.resume,
      openDate: e.created,
      ncType: (e.prm_data
        ? intl.formatMessage({ id: 'prm' })
        : intl.formatMessage({ id: 'resistance' })
      )?.toUpperCase(),
      csvFileName: e.file_name || '-',
    }));
  };

  const onTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedRows([]);
    setTableState({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      filters: '',
    });
    setInitialFilters(undefined);
    if (tab === Tabs.PRM) {
      navigate(`/app/project/${pid}/quality/ocr/prm`);
    }
  };

  const downloadCSV = (data, filename) => {
    const blob = new Blob(['\ufeff' + data], {
      type: 'text/csv;charset=utf-8',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    a.click();
    a.remove();
  };

  const onBack = () => navigate(-1);

  useEffect(() => {
    const isAnyUnusedNC = async () => {
      if (list) {
        try {
          const unused = await getUnusedNC(pid);
          setGenerateCSVButtonState(unused.data.total !== 0);
        } catch (e) {
          console.error(e);
        }
      }
    };
    if (selectedTab === Tabs.NC) {
      isAnyUnusedNC();
    }
  }, [list, pid, selectedTab]);

  useEffect(() => {
    setError(errorMessages);
  }, [errorMessages, setError]);

  useEffect(() => {
    if (ncId && pathname.includes('nc')) {
      setSelectedTab(Tabs.NC);
      setInitialFilters(() => ({ id: ncId }));
    }
  }, [ncId, pathname]);

  const onEdit = (nc) => {
    navigate(`/app/project/${pid}/quality/ocr/nonConformity/${nc.id}`);
  };
  return (
    <>
      <AccionaTableTabs
        selectedRows={selectedRows}
        selectedTab={selectedTab}
        setSelectedTab={onTabChange}
        handleOnBulkDeleting={handleOnBulkDeleting}
        onBack={onBack}
        tabs={[Tabs.PRM, Tabs.NC]}
        title={intl.formatMessage({ id: 'prms/ncs.table.title' })}
        titleClassName={styles.title}
        tabsClassName={styles.tabs}
        actions={
          <>
            {selectedTab === Tabs.NC && (
              <AccionaButton
                onClick={async () => {
                  if (pid) {
                    try {
                      await getCSVByProject(pid);
                      getNCsList({
                        size: tableState.pageSize,
                        page: tableState.current,
                        filters: { pid, ...tableState.filters },
                      });
                    } catch (e) {
                      console.error(e);
                    }
                  }
                }}
                msg={intl.formatMessage({ id: 'button.generate.CSV' })}
                classIcon="icon fas fa-file-csv"
                disabled={!generateCSVButtonState || !me?.is_active}
              />
            )}
            <AccionaButton
              id="deleteBtn"
              disabled={!selectedRows.length || !me?.is_active}
              onClick={() => {
                setShowRemoveModal(true);
              }}
              msg={intl.formatMessage({ id: 'button.delete' })}
              classIcon="icon fas fa-trash-alt"
            />
          </>
        }
        render={() => {
          switch (selectedTab) {
            case Tabs.PRM:
              return (
                <AccionaTable
                  key={Tabs.PRM}
                  columns={PRMS_COLUMNS_HEADERS}
                  getItems={getPRMsList}
                  entity="prms"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  showTitle={false}
                  dataParseFunction={parseFunction}
                  onChange={setTableState}
                  isSelectionDisabled={(prm) => prm.non_conformity}
                  actions={(prm) => (
                    <>
                      <AccionaTableButton
                        onClick={() =>
                          navigate(
                            `/app/project/${project.id_project}/quality/ocr/prm/${prm.id}`,
                          )
                        }
                        type="view"
                        toolTip={intl.formatMessage({
                          id: PRMS_BUTTONS.viewAction.tooltip,
                        })}
                      />
                      {!prm.non_conformity ? (
                        <AccionaTableButton
                          onClick={() => onSingleDelete(prm)}
                          type="delete"
                          disabled={!me?.is_active}
                          toolTip={intl.formatMessage({
                            id: PRMS_BUTTONS.deleteAction.tooltip,
                          })}
                        />
                      ) : (
                        <AccionaLabel>NC</AccionaLabel>
                      )}
                    </>
                  )}
                />
              );
            case Tabs.NC:
              return (
                <AccionaTable
                  key={Tabs.NC}
                  columns={NC_COLUMNS_HEADERS}
                  getItems={getNCsList}
                  entity="ncs"
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  showTitle={false}
                  dataParseFunction={parseFunction}
                  initialFilters={initialFilters}
                  onChange={setTableState}
                  isSelectionDisabled={() => true}
                  actions={(nc) => (
                    <>
                      <AccionaTableButton
                        onClick={async () => {
                          const csv = await getCSV(nc.file_name);
                          if (csv.data) {
                            downloadCSV(csv.data, nc.file_name);
                          }
                        }}
                        type="csv"
                        toolTip={intl.formatMessage({
                          id: NC_BUTTONS.downloadCSV.tooltip,
                        })}
                        disabled={nc.file_name === null || !me?.is_active}
                      />
                      <AccionaTableButton
                        onClick={() => onEdit(nc)}
                        type="edit"
                        disabled={!me?.is_active}
                        toolTip={intl.formatMessage({
                          id: NC_BUTTONS.editAction.tooltip,
                        })}
                      />
                      <AccionaTableButton
                        onClick={() => onSingleDelete(nc)}
                        type="delete"
                        disabled={!me?.is_active}
                        toolTip={intl.formatMessage({
                          id: NC_BUTTONS.deleteAction.tooltip,
                        })}
                      />
                    </>
                  )}
                />
              );
            default:
              return null;
          }
        }}
      />
      <RemoveModal
        entity="scannedDocuments"
        selectedRows={selectedRows}
        msg={intl.formatMessage({
          id:
            selectedRows.length > 1
              ? 'delete.scannedDocuments'
              : 'delete.scannedDocument',
        })}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
    </>
  );
};
const mapStateToProps = ({ prms }) => ({
  errorMessages: prms.errorMessages,
});

const matchDispachToProps = {
  getPRMsList: PRMActions.getPRMs,
  getNCsList: NCActions.getNCs,
  deletePRM: PRMActions.deletePRM,
  deleteNC: NCActions.deleteNC,
  clearErrors: PRMActions.clearErrors,
  createErrors: PRMActions.createErrors,
};

PRMsList.defaultProps = {
  errorMessages: {},
  selectedRows: [],
};

PRMsList.propTypes = {
  getPRMsList: PropTypes.func.isRequired,
  getNCsList: PropTypes.func.isRequired,
  deletePRM: PropTypes.func.isRequired,
  deleteNC: PropTypes.func.isRequired,
  errorMessages: PropTypes.shape({
    status: PropTypes.number,
  }),
  clearErrors: PropTypes.func.isRequired,
  createErrors: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
};

export default connect(mapStateToProps, matchDispachToProps)(PRMsList);
